import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft,
  Edit2,
  Package,
  Boxes,
  Truck,
  Repeat,
  FileSpreadsheet,
  FileText,
  Share2,
  Calendar,
  Building2,
  Layers,
  Coins,
  IndianRupee,
  CheckCircle2,
  Clock,
  Car
} from 'lucide-react';
import { type LucideIcon } from 'lucide-react';
import { formatMoney } from '@vns/core';
import { useMaterialDetail } from '../lib/directory';
import {
  exportExcelSections,
  exportPdfSections,
  sharePdfSections,
  canShare,
  type ExportSection,
  type ExportMeta,
} from '../lib/export';
import Shell from '../components/Shell';
import { MaterialDrawer } from './Materials';
import {
  EmptyState,
  TableSkeleton,
  ErrorState,
  formatDate,
  initials,
  useToast,
} from '../components/ui';

type Tab = 'purchases' | 'usage' | 'transfers';

const TABS: Array<{ key: Tab; label: string; icon: LucideIcon }> = [
  { key: 'purchases', label: 'Purchases & Invoices', icon: Truck },
  { key: 'usage', label: 'Site Usage History', icon: Boxes },
  { key: 'transfers', label: 'Stock Transfers', icon: Repeat },
];

const qty = (n: number) => n.toLocaleString('en-IN', { maximumFractionDigits: 3 });

export default function MaterialDetail() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [tab, setTab] = useState<Tab>('purchases');
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState<'excel' | 'pdf' | 'share' | null>(null);
  const { data, isLoading, error, refetch } = useMaterialDetail(id);

  const unit = data?.unitName ? ` ${data.unitName}` : '';

  function buildSections(): ExportSection[] {
    if (!data) return [];
    return [
      {
        title: 'Purchases & Billings History',
        columns: [
          { key: 'date', label: 'Date' },
          { key: 'project', label: 'Project' },
          { key: 'supplier', label: 'Supplier' },
          { key: 'invoice', label: 'Invoice No.' },
          { key: 'quantity', label: 'Qty', numeric: true },
          { key: 'unit', label: 'Unit' },
          { key: 'rate', label: 'Rate', numeric: true, money: true },
          { key: 'amount', label: 'Total', numeric: true, money: true },
          { key: 'paid', label: 'Paid', numeric: true, money: true },
          { key: 'balance', label: 'Balance', numeric: true, money: true },
        ],
        rows: data.purchases.map((p) => ({
          date: formatDate(p.date),
          project: p.projectName,
          supplier: p.supplierName,
          invoice: p.invoiceNo || '—',
          quantity: qty(p.quantity),
          unit: data.unitName || '—',
          rate: formatMoney(p.ratePaise),
          amount: formatMoney(p.amountPaise),
          paid: formatMoney(p.advancePaidPaise),
          balance: formatMoney(p.balancePaise),
        })),
      },
      {
        title: 'Usage History',
        columns: [
          { key: 'date', label: 'Date' },
          { key: 'project', label: 'Project' },
          { key: 'quantity', label: 'Quantity Used', numeric: true },
          { key: 'balance', label: 'Stock Balance', numeric: true },
          { key: 'remarks', label: 'Remarks' },
        ],
        rows: data.usage.map((u) => ({
          date: formatDate(u.date),
          project: u.projectName,
          quantity: qty(u.quantity),
          balance: `${qty(u.balance)}${unit}`,
          remarks: u.remarks || '—',
        })),
      },
      {
        title: 'Stock Transfers',
        columns: [
          { key: 'date', label: 'Date' },
          { key: 'from', label: 'From Project' },
          { key: 'to', label: 'To Project' },
          { key: 'quantity', label: 'Quantity', numeric: true },
          { key: 'vehicle', label: 'Vehicle No.' },
          { key: 'driver', label: 'Driver' },
          { key: 'remarks', label: 'Remarks' },
        ],
        rows: data.transfers.map((t) => ({
          date: formatDate(t.date),
          from: t.fromProjectName,
          to: t.toProjectName,
          quantity: `${qty(t.quantity)}${unit}`,
          vehicle: t.vehicleNo || '—',
          driver: t.driverName || '—',
          remarks: t.remarks || '—',
        })),
      },
    ];
  }

  function buildMeta(): ExportMeta {
    return {
      title: `Material Report \u2014 ${data?.name ?? 'Material'}`,
      subtitle: `Rate: ${formatMoney(data?.ratePaise ?? 0)} \u00b7 Stock on Hand: ${qty(data?.stockOnHand ?? 0)}${unit}`,
      filters: [
        `Supplier: ${data?.supplierName ?? '—'}`,
        `Brand: ${data?.brand ?? '—'}`,
        `Total Purchased: ${qty(data?.totalPurchased ?? 0)}${unit}`,
        `Total Used: ${qty(data?.totalUsed ?? 0)}${unit}`,
      ],
    };
  }

  async function runExport(kind: 'excel' | 'pdf') {
    if (!data) return;
    setBusy(kind);
    try {
      const sections = buildSections();
      const meta = buildMeta();
      if (kind === 'excel') {
        await exportExcelSections(sections, meta);
        toast.show('Material Report Excel downloaded');
      } else {
        await exportPdfSections(sections, meta);
        toast.show('Material Report PDF downloaded');
      }
    } catch (e) {
      toast.show(e instanceof Error ? e.message : 'Could not export file', true);
    } finally {
      setBusy(null);
    }
  }

  async function runShare() {
    if (!data) return;
    setBusy('share');
    try {
      const sections = buildSections();
      const meta = buildMeta();
      const shared = await sharePdfSections(sections, meta);
      if (!shared) toast.show('Sharing is not supported on this device', true);
    } catch (e) {
      toast.show(e instanceof Error ? e.message : 'Could not share file', true);
    } finally {
      setBusy(null);
    }
  }

  // Totals for purchase tab
  const totalPurchaseAmount = data?.purchases.reduce((s, p) => s + p.amountPaise, 0) ?? 0;
  const totalPurchasePaid = data?.purchases.reduce((s, p) => s + p.advancePaidPaise, 0) ?? 0;
  const totalPurchaseBalance = data?.purchases.reduce((s, p) => s + p.balancePaise, 0) ?? 0;
  const totalPurchaseQty = data?.purchases.reduce((s, p) => s + p.quantity, 0) ?? 0;

  // Totals for usage tab
  const totalUsageQty = data?.usage.reduce((s, u) => s + u.quantity, 0) ?? 0;

  // Totals for transfer tab
  const totalTransferQty = data?.transfers.reduce((s, t) => s + t.quantity, 0) ?? 0;

  return (
    <Shell
      title={data?.name ?? 'Material Details'}
      crumb="Materials"
      actions={
        data && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              type="button"
              className="btn btn-auto btn-sm"
              disabled={!!busy}
              onClick={() => void runExport('excel')}
              style={{ display: 'flex', alignItems: 'center', gap: 6, borderRadius: 'var(--radius-sm)' }}
            >
              <FileSpreadsheet size={13} />
              <span>{busy === 'excel' ? 'Exporting…' : 'Excel'}</span>
            </button>
            <button
              type="button"
              className="btn btn-auto btn-sm"
              disabled={!!busy}
              onClick={() => void runExport('pdf')}
              style={{ display: 'flex', alignItems: 'center', gap: 6, borderRadius: 'var(--radius-sm)' }}
            >
              <FileText size={13} />
              <span>{busy === 'pdf' ? 'Exporting…' : 'PDF'}</span>
            </button>
            {canShare() && (
              <button
                type="button"
                className="btn btn-auto btn-sm"
                disabled={!!busy}
                onClick={() => void runShare()}
                style={{ display: 'flex', alignItems: 'center', gap: 6, borderRadius: 'var(--radius-sm)' }}
              >
                <Share2 size={13} />
                <span>{busy === 'share' ? 'Sharing…' : 'Share'}</span>
              </button>
            )}
            <button
              type="button"
              className="btn btn-auto btn-sm"
              onClick={() => setEditing(true)}
              style={{ display: 'flex', alignItems: 'center', gap: 6, borderRadius: 'var(--radius-sm)' }}
            >
              <Edit2 size={13} />
              <span>Edit Material</span>
            </button>
          </div>
        )
      }
    >
      <button
        className="btn btn-auto btn-sm btn-ghost"
        style={{
          marginBottom: 16,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          color: 'var(--text-soft)',
          paddingLeft: 4,
        }}
        onClick={() => navigate('/materials')}
      >
        <ArrowLeft size={14} />
        <span>Back to Materials</span>
      </button>

      {isLoading ? (
        <div className="card"><TableSkeleton rows={4} cols={4} /></div>
      ) : error ? (
        <div className="card"><ErrorState error={error} onRetry={() => void refetch()} /></div>
      ) : !data ? (
        <div className="card"><EmptyState icon="box" title="Material not found" /></div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25 }}
        >
          {/* Material Identity Card */}
          <div
            className="card"
            style={{
              padding: '20px 24px',
              marginBottom: 20,
              background: 'var(--surface)',
              border: '1px solid var(--border)',
            }}
          >
            <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <div
                className="avatar-sq"
                style={{
                  width: 58,
                  height: 58,
                  fontSize: 18,
                  fontWeight: 700,
                  borderRadius: 'var(--radius)',
                  background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.12), rgba(37, 99, 235, 0.04))',
                  color: 'var(--brand)',
                  border: '1px solid rgba(37, 99, 235, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {initials(data.name)}
              </div>

              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0, color: 'var(--text)' }}>
                    {data.name}
                  </h2>
                  {data.unitName && (
                    <span
                      style={{
                        fontSize: 11.5,
                        fontWeight: 600,
                        padding: '2px 8px',
                        borderRadius: 'var(--radius-xs)',
                        background: 'var(--surface-muted)',
                        border: '1px solid var(--border)',
                        color: 'var(--text-soft)',
                      }}
                    >
                      Unit: {data.unitName}
                    </span>
                  )}
                </div>

                <div
                  style={{
                    display: 'flex',
                    gap: 16,
                    flexWrap: 'wrap',
                    marginTop: 8,
                    fontSize: 13,
                    color: 'var(--text-soft)',
                    alignItems: 'center',
                  }}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    <Layers size={13} style={{ color: 'var(--text-faint)' }} />
                    <span>Brand: {data.brand || 'Generic / Unbranded'}</span>
                  </span>

                  {data.supplierName && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                      <Truck size={13} style={{ color: 'var(--text-faint)' }} />
                      <span>Default Supplier: {data.supplierName}</span>
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Stock & Rate KPI Metrics */}
          <div className="kpi-row" style={{ marginBottom: 20 }}>
            <div className="kpi">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <span className="lbl">Standard Rate</span>
                <div style={{ color: 'var(--accent)', background: 'rgba(249, 115, 22, 0.1)', padding: 5, borderRadius: 'var(--radius-xs)' }}>
                  <IndianRupee size={14} />
                </div>
              </div>
              <div className="val num" style={{ fontSize: 20 }}>{formatMoney(data.ratePaise)}</div>
              <div style={{ fontSize: 11.5, color: 'var(--text-faint)', marginTop: 4 }}>Standard procurement cost</div>
            </div>

            <div className="kpi">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <span className="lbl">Total Purchased</span>
                <div style={{ color: '#2563eb', background: 'rgba(37, 99, 235, 0.1)', padding: 5, borderRadius: 'var(--radius-xs)' }}>
                  <Truck size={14} />
                </div>
              </div>
              <div className="val num" style={{ fontSize: 20, color: '#2563eb' }}>{qty(data.totalPurchased)}{unit}</div>
              <div style={{ fontSize: 11.5, color: 'var(--text-faint)', marginTop: 4 }}>Cumulative receipts</div>
            </div>

            <div className="kpi">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <span className="lbl">Total Site Usage</span>
                <div style={{ color: '#8b5cf6', background: 'rgba(139, 92, 246, 0.1)', padding: 5, borderRadius: 'var(--radius-xs)' }}>
                  <Boxes size={14} />
                </div>
              </div>
              <div className="val num" style={{ fontSize: 20, color: '#8b5cf6' }}>{qty(data.totalUsed)}{unit}</div>
              <div style={{ fontSize: 11.5, color: 'var(--text-faint)', marginTop: 4 }}>Logged consumption across sites</div>
            </div>

            <div className="kpi">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <span className="lbl">Stock on Hand</span>
                <div
                  style={{
                    color: data.stockOnHand > 0 ? '#16a34a' : '#ea580c',
                    background: data.stockOnHand > 0 ? 'rgba(22, 163, 74, 0.1)' : 'rgba(234, 88, 12, 0.1)',
                    padding: 5,
                    borderRadius: 'var(--radius-xs)',
                  }}
                >
                  <Package size={14} />
                </div>
              </div>
              <div
                className="val num"
                style={{
                  fontSize: 20,
                  color: data.stockOnHand > 0 ? '#16a34a' : 'var(--text-soft)',
                }}
              >
                {qty(data.stockOnHand)}{unit}
              </div>
              <div style={{ fontSize: 11.5, color: 'var(--text-faint)', marginTop: 4 }}>
                {data.stockOnHand > 0 ? 'Available current inventory' : 'Zero physical stock'}
              </div>
            </div>
          </div>

          {/* Tabbed Ledgers */}
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div
              style={{
                display: 'flex',
                gap: 8,
                padding: '12px 18px',
                borderBottom: '1px solid var(--border)',
                background: 'var(--surface-muted)',
                overflowX: 'auto',
              }}
            >
              {TABS.map((t) => {
                const IconComp = t.icon;
                const active = tab === t.key;
                const count =
                  t.key === 'purchases'
                    ? data.purchases.length
                    : t.key === 'usage'
                    ? data.usage.length
                    : data.transfers.length;

                return (
                  <button
                    key={t.key}
                    onClick={() => setTab(t.key)}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 7,
                      padding: '8px 14px',
                      fontSize: 13,
                      fontWeight: active ? 600 : 500,
                      cursor: 'pointer',
                      borderRadius: 'var(--radius-sm)',
                      background: active ? 'var(--surface)' : 'transparent',
                      color: active ? 'var(--text)' : 'var(--text-soft)',
                      border: active ? '1px solid var(--border)' : '1px solid transparent',
                      boxShadow: active ? '0 1px 3px rgba(0,0,0,0.04)' : 'none',
                      transition: 'all 0.15s ease',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <IconComp size={14} style={{ color: active ? 'var(--brand)' : 'var(--text-faint)' }} />
                    <span>{t.label}</span>
                    <span
                      style={{
                        fontSize: 11,
                        padding: '1px 6px',
                        borderRadius: 'var(--radius-xs)',
                        background: active ? 'rgba(37, 99, 235, 0.12)' : 'var(--surface)',
                        color: active ? 'var(--brand)' : 'var(--text-faint)',
                        border: '1px solid var(--border)',
                        fontWeight: 600,
                      }}
                    >
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="table-wrap">
              <AnimatePresence mode="wait">
                {tab === 'purchases' && (
                  <motion.div
                    key="purchases-table"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                  >
                    {!data.purchases.length ? (
                      <EmptyState
                        icon="truck"
                        title="No purchases recorded yet"
                        hint="Purchase orders and vendor receipts for this material will appear here."
                      />
                    ) : (
                      <table>
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Project</th>
                            <th>Supplier</th>
                            <th>Invoice No.</th>
                            <th>Quantity</th>
                            <th>Rate</th>
                            <th>Total Amount</th>
                            <th>Paid</th>
                            <th>Balance</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.purchases.map((p) => (
                            <tr
                              key={p.id}
                              className="clickable"
                              onClick={() => navigate(`/projects/${p.projectId}`)}
                            >
                              <td>
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: 'var(--text-soft)' }}>
                                  <Calendar size={12} style={{ color: 'var(--text-faint)' }} />
                                  <span>{formatDate(p.date)}</span>
                                </span>
                              </td>
                              <td>
                                <span style={{ fontWeight: 600, color: 'var(--text)' }}>
                                  {p.projectName}
                                </span>
                              </td>
                              <td>
                                <span style={{ color: 'var(--text-soft)' }}>
                                  {p.supplierName}
                                </span>
                              </td>
                              <td>
                                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>
                                  {p.invoiceNo || '—'}
                                </span>
                              </td>
                              <td className="num">
                                <span style={{ fontWeight: 600 }}>{qty(p.quantity)}{unit}</span>
                              </td>
                              <td className="num">
                                <span style={{ color: 'var(--text-soft)' }}>
                                  {formatMoney(p.ratePaise)}
                                </span>
                              </td>
                              <td className="num" style={{ fontWeight: 600, color: 'var(--text)' }}>
                                {formatMoney(p.amountPaise)}
                              </td>
                              <td className="num" style={{ fontWeight: 600, color: '#16a34a' }}>
                                {formatMoney(p.advancePaidPaise)}
                              </td>
                              <td
                                className="num"
                                style={{
                                  fontWeight: 600,
                                  color: p.balancePaise > 0 ? '#ea580c' : 'var(--text-faint)',
                                }}
                              >
                                {formatMoney(p.balancePaise)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr>
                            <td colSpan={4} style={{ fontWeight: 700 }}>
                              Total ({data.purchases.length} invoices)
                            </td>
                            <td className="num" style={{ fontWeight: 700 }}>
                              {qty(totalPurchaseQty)}{unit}
                            </td>
                            <td />
                            <td className="num" style={{ fontWeight: 700 }}>
                              {formatMoney(totalPurchaseAmount)}
                            </td>
                            <td className="num" style={{ fontWeight: 700, color: '#16a34a' }}>
                              {formatMoney(totalPurchasePaid)}
                            </td>
                            <td
                              className="num"
                              style={{
                                fontWeight: 700,
                                color: totalPurchaseBalance > 0 ? '#ea580c' : 'var(--text-faint)',
                              }}
                            >
                              {formatMoney(totalPurchaseBalance)}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    )}
                  </motion.div>
                )}

                {tab === 'usage' && (
                  <motion.div
                    key="usage-table"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                  >
                    {!data.usage.length ? (
                      <EmptyState
                        icon="box"
                        title="No usage logged yet"
                        hint="Material consumption and site usage reports will appear here."
                      />
                    ) : (
                      <table>
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Project</th>
                            <th>Quantity Used</th>
                            <th>Stock Balance After Entry</th>
                            <th>Remarks</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.usage.map((u) => (
                            <tr
                              key={u.id}
                              className="clickable"
                              onClick={() => navigate(`/projects/${u.projectId}`)}
                            >
                              <td>
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: 'var(--text-soft)' }}>
                                  <Calendar size={12} style={{ color: 'var(--text-faint)' }} />
                                  <span>{formatDate(u.date)}</span>
                                </span>
                              </td>
                              <td>
                                <span style={{ fontWeight: 600, color: 'var(--text)' }}>
                                  {u.projectName}
                                </span>
                              </td>
                              <td className="num" style={{ fontWeight: 600, color: 'var(--text)' }}>
                                {qty(u.quantity)}{unit}
                              </td>
                              <td className="num">
                                <span style={{ color: 'var(--text-soft)' }}>
                                  {qty(u.balance)}{unit}
                                </span>
                              </td>
                              <td style={{ color: 'var(--text-soft)', fontSize: 12.5 }}>
                                {u.remarks || '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr>
                            <td colSpan={2} style={{ fontWeight: 700 }}>
                              Total Consumption ({data.usage.length} entries)
                            </td>
                            <td className="num" style={{ fontWeight: 700 }}>
                              {qty(totalUsageQty)}{unit}
                            </td>
                            <td colSpan={2} />
                          </tr>
                        </tfoot>
                      </table>
                    )}
                  </motion.div>
                )}

                {tab === 'transfers' && (
                  <motion.div
                    key="transfers-table"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                  >
                    {!data.transfers.length ? (
                      <EmptyState
                        icon="repeat"
                        title="No stock transfers recorded"
                        hint="Movement of this material between project sites will appear here."
                      />
                    ) : (
                      <table>
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>From Site</th>
                            <th>To Site</th>
                            <th>Quantity</th>
                            <th className="num">Rate</th>
                            <th className="num">Total Value</th>
                            <th>Vehicle No.</th>
                            <th>Driver</th>
                            <th>Remarks</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.transfers.map((t) => (
                            <tr key={t.id}>
                              <td>
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: 'var(--text-soft)' }}>
                                  <Calendar size={12} style={{ color: 'var(--text-faint)' }} />
                                  <span>{formatDate(t.date)}</span>
                                </span>
                              </td>
                              <td>
                                <span style={{ fontWeight: 500, color: 'var(--text)' }}>
                                  {t.fromProjectName}
                                </span>
                              </td>
                              <td>
                                <span style={{ fontWeight: 600, color: 'var(--text)' }}>
                                  {t.toProjectName}
                                </span>
                              </td>
                              <td className="num" style={{ fontWeight: 600, color: 'var(--text)' }}>
                                {qty(t.quantity)}{unit}
                              </td>
                              <td className="num">
                                <span style={{ color: 'var(--text-soft)' }}>
                                  {t.ratePaise != null ? formatMoney(t.ratePaise) : '—'}
                                </span>
                              </td>
                              <td className="num" style={{ fontWeight: 600, color: t.amountPaise ? 'var(--text)' : 'var(--text-faint)' }}>
                                {t.amountPaise != null ? formatMoney(t.amountPaise) : '—'}
                              </td>
                              <td>
                                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>
                                  {t.vehicleNo || '—'}
                                </span>
                              </td>
                              <td style={{ color: 'var(--text-soft)' }}>
                                {t.driverName || '—'}
                              </td>
                              <td style={{ color: 'var(--text-soft)', fontSize: 12.5 }}>
                                {t.remarks || '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr>
                            <td colSpan={3} style={{ fontWeight: 700 }}>
                              Total Transferred ({data.transfers.length} trips)
                            </td>
                            <td className="num" style={{ fontWeight: 700 }}>
                              {qty(totalTransferQty)}{unit}
                            </td>
                            <td />
                            <td className="num" style={{ fontWeight: 700, color: '#0284c7' }}>
                              {data.transfers.reduce((s, t) => s + (t.amountPaise || 0), 0) > 0
                                ? formatMoney(data.transfers.reduce((s, t) => s + (t.amountPaise || 0), 0))
                                : '—'}
                            </td>
                            <td colSpan={3} />
                          </tr>
                        </tfoot>
                      </table>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Edit Material Drawer */}
          {editing && (
            <MaterialDrawer
              existing={{
                id: data.id,
                name: data.name,
                brand: data.brand,
                unitName: data.unitName,
                ratePaise: data.ratePaise,
                supplierName: data.supplierName,
                stock: data.stockOnHand,
                openingQty: 0,
                unitId: null,
                supplierId: null,
                categoryId: null,
                categoryName: null,
              }}
              onClose={() => {
                setEditing(false);
                void refetch();
              }}
            />
          )}
        </motion.div>
      )}
    </Shell>
  );
}
